/*
  Payload shapes for the Phase 2 deep-dive routes, transcribed from
  `frontend-api-contract.md` (status date 2026-09-05).

  These are the wire shapes — snake_case, nullable where the contract says a
  number can be unknown. Nothing here is reshaped for the UI; that happens in
  `lib/deep-dive-adapters.ts`, so a contract change lands in one file and the
  compiler points at every screen that cared.

  The contract's one rule that outranks the types: **null means unknown, never
  zero**. Every `| null` below is a "render n/a", not a "render 0".
*/

/* ----------------------------------------------------------------- services */

/** Systems with health + sales endpoints. Path segments, used verbatim. */
export type DeepDiveService = 'aurora' | 'solo' | 'docusign' | 'one-portal'

/**
 * The `platform` a sales route reports itself as, and what `sales/filters` takes.
 * Note the underscore: the path segment is `one-portal`, the platform is
 * `one_portal`. They are not interchangeable — see `salesFilterPlatform`.
 */
export type SalesPlatform = 'aurora' | 'solo' | 'one_portal'

/** Freshness fields carried by every OWEHub-backed payload. */
export interface Freshness {
  /** Max modified time of the matched data; null when nothing matched. */
  data_as_of: string | null
  /** Source liveness — when the upstream table last changed at all. */
  table_last_modified?: string | null
  /** False = upstream sync is failing and the data may be stale. */
  sync_healthy?: boolean
  source_timezone?: string
}

/* ------------------------------------------------------------------- health */

/** Server-side status vocabulary for a service and for an incident transition. */
export type DeepDiveStatus =
  'healthy' | 'minor' | 'major' | 'critical' | 'maintenance' | 'vendor_silent'

export interface Uptime30d {
  from: string
  to: string
  /** null when nothing was observed in the window → "n/a", not 0%. */
  uptime_pct: number | null
  /** Share of the window actually observed. Shown beside the uptime figure. */
  known_coverage_pct: number | null
  up_seconds: number
  down_seconds: number
  unknown_seconds: number
  sample_count: number
}

export interface ServiceOverview extends Freshness {
  service: string
  status: DeepDiveStatus
  response_time_ms: number | null
  checked_at: string
  uptime_30d: Uptime30d
  /** The incident in progress, or null when the service is up. */
  open_incident: IncidentRow | null
}

/** Vendor indicator code as it arrives on the feed. */
export type IncidentIndicator =
  'CRITICAL_FAIL' | 'DEGRADED' | 'MINOR' | 'MAINTENANCE' | 'VENDOR_SILENT' | 'LATENCY_HIGH'

export interface IncidentRow {
  id: number
  service: string
  started_at: string
  /** null while the incident is still open. */
  ended_at: string | null
  from_status: DeepDiveStatus
  to_status: DeepDiveStatus
  indicator: IncidentIndicator
  /** null while the incident is still open. */
  duration_ms: number | null
  ongoing: boolean
}

export interface IncidentsPage {
  service: string
  from: string
  to: string
  total: number
  limit: number
  offset: number
  incidents: IncidentRow[]
}

export interface IncidentsQuery {
  /** RFC3339. Defaults to a trailing 30 days; the span may not exceed 365 days. */
  from?: string
  to?: string
  /** 1–200, default 25. */
  limit?: number
  offset?: number
}

/* -------------------------------------------------------------------- sales */

/**
 * The six filter dimensions, as query parameters. Repeatable, and matched
 * exactly and case-sensitively upstream — values go back verbatim, never trimmed.
 *
 * `region` is ~99% blank on Aurora/Solo (prefer `state` and `office`), and One
 * Portal rejects `office` with a 400 while treating `state` and `region` alike.
 */
export interface SalesFilterSelection {
  partner?: readonly string[]
  region?: readonly string[]
  finance?: readonly string[]
  dealer?: readonly string[]
  state?: readonly string[]
  office?: readonly string[]
}

export type SalesFilterKey = keyof SalesFilterSelection

/** Order the filter bar renders them in — state/office first, per the contract. */
export const SALES_FILTER_KEYS: readonly SalesFilterKey[] = [
  'state',
  'office',
  'dealer',
  'partner',
  'finance',
  'region',
]

export interface SalesExpectation {
  min: number | null
  max: number | null
  /** e.g. "p25-p75 of same weekday, trailing 30 days", or "insufficient history". */
  method: string
  sample_days: number
}

export interface SalesOverview extends Freshness {
  platform: SalesPlatform
  /** The business day these figures cover, in `timezone`. */
  date: string
  timezone: string
  sales_today: number | null
  /** Always 0 on One Portal — it has no cancellation concept. */
  cancelled_today: number | null
  kw_today: number | null
  expected: SalesExpectation
  weekday_daily_avg_30d: number | null
  /** Echo of the filters that were applied; `{}` when none. */
  filters: Partial<Record<SalesFilterKey, string[]>>
  filters_applied?: unknown
}

export interface TimelineHourPoint {
  /** 0–23 in the business timezone. Exactly 24 points are always returned. */
  hour: number
  actual: number
  cumulative_actual: number
  baseline: number
  cumulative_baseline: number
}

export interface SalesTimelinePayload extends Freshness {
  platform: SalesPlatform
  date: string
  timezone: string
  points: TimelineHourPoint[]
  /** 23 when `date` is not today. */
  current_hour: number
  current_hour_sales: number | null
  baseline_this_hour: number | null
  baseline_days: number
  baseline_note: string
  filters: Partial<Record<SalesFilterKey, string[]>>
}

export interface QuarterRow {
  /** "Q1" … "Q4". All four are always present; future quarters are zeros. */
  quarter: string
  sales_count: number
  cancelled_count: number
  kw_total: number
  /** null on One Portal — their API carries no revenue. */
  contract_value_total: number | null
  contract_value_unparsed_count: number
}

export interface SalesQuarterlyPayload extends Freshness {
  platform: SalesPlatform
  year: number
  quarters: QuarterRow[]
  filters: Partial<Record<SalesFilterKey, string[]>>
}

/* ---------------------------------------------------------- filter catalogue */

/** One selectable value, with how many rows carry it. */
export interface FilterValue {
  value: string
  count: number
  cancelled_count?: number
}

export interface FilterDimension {
  param: string
  blank_count?: number
  blank_pct?: number
  /** Upstream's own warning, e.g. region's "…not geography… use state or office". */
  note?: string
  values: FilterValue[]
}

/** Aurora and Solo: a dimension map with counts. */
export interface RichFilterCatalogue {
  platform: 'aurora' | 'solo'
  values: {
    total_rows: number
    dimensions: Partial<Record<SalesFilterKey, FilterDimension>>
    cached_at?: string
    cache_ttl_seconds?: number
  }
}

/** One Portal: flat arrays of opaque ids, no counts. */
export interface FlatFilterCatalogue {
  platform: 'one_portal'
  values: {
    partners?: string[]
    regions?: string[]
    dealers?: string[]
    finance_statuses?: string[]
    statuses?: string[]
  }
}

/** Branch on `platform` — the two shapes have nothing in common but that field. */
export type FilterCatalogue = RichFilterCatalogue | FlatFilterCatalogue

/* ---------------------------------------------------------------- messaging */

/**
 * Which route family a messaging call goes to — and with it, which channel.
 *
 * The two are bound: `sendgrid/messaging/*` serves email and `twilio/messaging/*`
 * serves SMS. This replaces the `channel` parameter the routes used to take;
 * asking one family for the other's channel is now a 400 whose message names the
 * right family ("email figures live under /sendgrid/messaging/*"), so nothing
 * here may send `channel` at all.
 *
 * **There is no combined view.** The two families are different vendors
 * measuring different things and are never summed — nothing in this codebase may
 * add a figure from one to the other.
 */
export type MessagingProvider = 'sendgrid' | 'twilio'

/**
 * The channel a payload says it covers, read off `source.channel`.
 *
 * Descriptive, not a request parameter: it is what the backend reports having
 * counted. Labels come from it rather than from the route we called, so a screen
 * cannot claim the wrong channel if a family is ever repointed.
 */
export type MessagingChannel = 'email' | 'sms'

/**
 * Who actually sends the notifications on the selected channel — SendGrid for
 * email, Twilio proper for SMS. Read this rather than assuming from the route
 * name, which says "twilio" for both.
 */
export interface MessagingSource {
  display_name: string
  provider: string
  channel: string
}

/** Window sizes the messaging routes accept. `7d` is null until sync catches up. */
export type MessagingWindow = '24h' | '48h' | '7d'

export interface MessagingMetric {
  /** null = the provider cannot report it, or the window predates sync coverage. */
  value: number | null
  previous: number | null
  /** null → hide the arrow rather than drawing a 0% move. */
  delta_pct: number | null
}

export interface MessagingSyncState {
  first_success_at: string | null
  last_success_at: string | null
  last_status: string
  last_error: string | null
  coverage_start: string | null
  interval_seconds: number
  stale_after_seconds: number
}

export interface MessagingOverview extends Freshness {
  source: MessagingSource
  window: MessagingWindow
  message_type: string
  sent: MessagingMetric
  /** Of those sent, the ones the far end accepted. */
  delivered: MessagingMetric
  /** bounced + blocked + dropped. */
  rejected: MessagingMetric
  /**
   * `rejected` as a share of `sent`, computed by the backend.
   *
   * Preferred over dividing the two here: the backend's figure is the one its
   * own alert thresholds are measured against, and a second, locally rounded
   * version of the same ratio is how a tile and a banner end up disagreeing.
   */
  rejected_pct: MessagingMetric
  /** null on SMS — Twilio publishes no spam-report feed at all. */
  spam_reports: MessagingMetric
  deliverability_pct: MessagingMetric
  /** Caveats about how each figure was counted — surfaced in an info tooltip. */
  notes: string[]
  sync?: MessagingSyncState
}

export interface MessagingHourBucket {
  /** Start of the hour, UTC ISO. Every hour in the window is present. */
  hour: string
  sent: number
  rejected: number
}

export interface MessagingHourly extends Freshness {
  source: MessagingSource
  window: MessagingWindow
  buckets: MessagingHourBucket[]
  notes: string[]
}

/**
 * What became of a failed message.
 *
 * The vocabulary differs by channel: email reports bounced/blocked/dropped,
 * SMS reports blocked/carrier_filtered. `unknown` occurs on both — and on SMS it
 * carries the STOP replies (Twilio 21610), which are a recipient's choice rather
 * than a carrier refusing us, and must not be coloured as a block.
 */
export type RejectionOutcome = 'bounced' | 'blocked' | 'dropped' | 'carrier_filtered' | 'unknown'

export interface RejectionRow {
  timestamp: string
  /** Masked upstream — "p***@domain.com" for email, "***-***-2882" for SMS. */
  recipient_masked: string
  message_type: string
  outcome: RejectionOutcome
  /** SendGrid's wording ("Bounced Address") or a Twilio error code ("30006"). */
  reason_code: string | null
  reason: string | null
  sent_at: string | null
  /** Present since the channel split; the row's own channel and sender. */
  channel?: MessagingChannel
  provider?: string
}

export interface RejectionsPage extends Freshness {
  source: MessagingSource
  rejections: RejectionRow[]
  /** Absent on the last page; pass it back as `cursor` for the next one. */
  next_cursor?: string
  notes: string[]
}

export interface MessagingAlerts extends Freshness {
  source: MessagingSource
  /** An inference, not a vendor verdict — the banner must word it that way. */
  blockage_suspected: boolean
  reason: string
  rejection_rate_pct: number | null
  window: string
  evidence: {
    reputation?: number | null
    /** Which rules fired. */
    signals?: string[]
    [key: string]: unknown
  }
  /** Where the on-call engineer actually fixes it — the vendor's own console. */
  console_url: string
  notes: string[]
}

/* ------------------------------------------------------------ vendor status */

/**
 * A vendor's own health, as `GET {vendor}/status` reports it.
 *
 * Two independent halves, and they must not be read as one number:
 *
 * · the **vendor's public status page** (`indicator`, `description`, `page_id`),
 *   which is the vendor talking about itself to everybody; and
 * · the **account probe** (`account`), which is this deployment's own
 *   credentials being exercised against the vendor's API right now.
 *
 * Either can be fine while the other is not — a vendor can be green while our
 * key is unconfigured, or degraded while our calls still succeed.
 */
export interface VendorStatusPayload {
  /** Statuspage severity: none | minor | major | critical | maintenance. */
  status: string
  indicator?: string
  /** The vendor's own wording, e.g. "Partially Degraded Service". */
  description?: string
  /**
   * What the verdict covers, e.g. "SendGrid components used by OWE".
   *
   * The feed judges only the components OWE depends on, not the whole vendor
   * status page — which matters because SendGrid and Twilio publish to one
   * shared page covering dozens of products.
   */
  scope?: string
  /** The depended-on components and their individual states. */
  components?: { name: string; group?: string; status: string }[]
  /** Open vendor incidents touching those components; [] when none. */
  incidents?: {
    name: string
    status: string
    impact: string
    url?: string
    updated_at?: string
  }[]
  /**
   * The vendor page overall. **Context only — never colour anything from it.**
   * It routinely reports trouble in products OWE never calls.
   */
  page_indicator?: string
  page_description?: string
  /** Present on vendor_silent; says why the read failed. */
  error_detail?: string
  /** Which status page was read. */
  page_id?: string
  page_updated_at?: string
  /** How long the status-page read took, not how fast the vendor's API is. */
  response_time_ms?: number | null
  account?: VendorAccountProbe
  checked_at?: string
}

export interface VendorAccountProbe {
  /** False when this deployment has no credentials for the vendor. */
  configured: boolean
  status?: string
  http_status?: number | null
  response_time_ms?: number | null
  /** The exact URL that was called — the probe's own receipt. */
  endpoint?: string
  /** Free text: today's account totals, or why it could not run. */
  detail?: string
  /** Environment variables that still need setting, when unconfigured. */
  missing_env?: string[]
  checked_at?: string
}

/* ---------------------------------------------------------------- envelopes */

/**
 * The fixed windows the envelope routes used to take.
 *
 * Superseded by `period` (see below), which every envelope route now accepts
 * and which the screen sends instead — the two are mutually exclusive on the
 * wire, and sending both is a 400. Kept only because a payload fetched before
 * the switch may still echo one back.
 */
export type EnvelopeWindow = '7d' | '30d' | '90d'

export interface EnvelopesOverview extends Freshness {
  /** Only present on a `window=` request; the screen sends `period` instead. */
  window?: EnvelopeWindow
  /** The dates the chosen period resolved to, echoed by the server. */
  period?: SalesPeriodWindow
  /** Set when the sync starts inside the period — the counts are for less than it. */
  coverage?: SalesCoverage
  /** Signer recipients sitting at sent/delivered on a live envelope. */
  pending_signatures: number | null
  /** Distinct envelopes among those recipients — always ≤ pending_signatures. */
  pending_envelopes?: number | null
  auth_failed_recipients?: number | null
  /** completed / (sent + delivered + signed + completed + declined). */
  sent_to_signed_pct: number | null
  /**
   * Envelope counts by **current** status — mutually exclusive buckets that sum
   * to `total`, not sequential funnel stages.
   *
   * The contract's example (125 → 106 → 88 → 81) reads like a funnel, and the
   * field name reinforces it, but live data disproves it: `completed` (1,764)
   * exceeds `sent` (634) because an envelope counted as completed is no longer
   * counted as sent. `pipeline` and `by_status` are byte-identical on the wire;
   * `by_status` is the one the notes document as exact and additive, so that is
   * the one the UI reads.
   */
  pipeline: Record<string, number>
  by_status: Record<string, number>
  /** Every envelope in the window, matching the sum of `by_status`. */
  total?: number
  /**
   * The DocuSign account is shared by production and staging and the sync covers
   * the whole account, so these counts are not production-only. Worth surfacing
   * rather than quietly presenting the total as ours.
   */
  by_environment?: Record<string, number>
  /** Named at last — the contract promised this once the vendor was settled. */
  provider?: string
  /** How each figure was counted; shown in an info tooltip. */
  notes?: string[]
}

/** What the row's action button offers. */
export type SuggestedAction = 'send_reminder' | 'resend_link' | 'none'

/** One row **per recipient** — an envelope with two signers is two rows. */
export interface EnvelopeRow {
  envelope_id: string
  recipient_id: string
  recipient: string
  company: string | null
  sent_at: string
  envelope_status: string
  /** sent | delivered | completed | declined | autoresponded | authenticationfailed… */
  recipient_status: string
  auth_failed: boolean
  office: string | null
  finance_partner: string | null
  suggested_action: SuggestedAction
}

export interface EnvelopesPage extends Freshness {
  rows: EnvelopeRow[]
  next_cursor?: string
  period?: SalesPeriodWindow
  coverage?: SalesCoverage
}

export interface EnvelopesQuery {
  /** Restricts the queue to envelopes sent inside the period. */
  period?: SalesPeriodSelection
  status?: string
  office?: string
  finance_partner?: string
  search?: string
  limit?: number
  cursor?: string
}

export type EnvelopeGroupBy = 'office' | 'finance_partner'

export interface EnvelopeGroups extends Freshness {
  by: EnvelopeGroupBy
  window?: EnvelopeWindow
  period?: SalesPeriodWindow
  coverage?: SalesCoverage
  total: number
  groups: { key: string; count: number; pct: number }[]
  /** Envelopes with no value for the grouping dimension. */
  unattributed: number
}

export interface EnvelopeActionResult {
  envelope_id: string
  recipient_id: string
  action: SuggestedAction
  accepted: boolean
  message: string
  /** Set on a 409 too — disable the button until this moment passes. */
  cooldown_until: string | null
}

/* ----------------------------------------------------- period sales (2026-09) */

/**
 * The periods `sales/summary` and `sales/series` accept.
 *
 * This replaces the old one-day `date=` parameter for anything wider than a day.
 * The two must never be sent together on the messaging and envelope routes —
 * `period` and `window` there are mutually exclusive and both is a 400.
 */
export type SalesPeriodKey =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom'

/** Whether the response carries a like-for-like previous period. */
export type SalesCompare = 'previous' | 'none'

export interface SalesPeriodSelection {
  key: SalesPeriodKey
  /** Inclusive `YYYY-MM-DD`; required for `custom`, ignored otherwise. */
  from?: string
  to?: string
  compare?: SalesCompare
}

export interface SalesPeriodWindow {
  key: SalesPeriodKey
  from: string
  to: string
  from_date: string
  to_date: string
  timezone: string
  /**
   * The x-axis the server chose: hour (≤2 days), day (≤93), month beyond that.
   * Never picked by the client — plotting a month series on an hour axis is how
   * a chart ends up lying about what it shows.
   */
  granularity: 'hour' | 'day' | 'month'
  days: number
  /** True while the period has not finished, so its total is still moving. */
  in_progress?: boolean
}

/** A figure and how it compares with the same span one step back. */
export interface SalesMetric {
  value: number | null
  previous: number | null
  /** null → no arrow. Never render it as a 0% move. */
  delta_pct: number | null
}

export interface SalesContractValue extends SalesMetric {
  unparsed_count: number
}

/**
 * How much of the requested span the source actually covers.
 *
 * `partial` means the data starts after the period does — One Portal only has
 * figures from 2026-04-04, Aurora and Solo from 2025-05-01 — and everything
 * before that is **unknown, not zero**.
 */
export interface SalesCoverage {
  partial: boolean
  covered_from?: string
  covered_to?: string
  requested_from?: string
  requested_to?: string
  note?: string
}

export interface SalesSummary {
  period: SalesPeriodWindow
  /** Absent when `compare=none` was sent. */
  previous?: { from: string; to: string; from_date: string; to_date: string }
  sales: SalesMetric
  cancelled: SalesMetric
  kw: SalesMetric
  /** `value` is always null on One Portal — their API carries no revenue. */
  contract_value: SalesContractValue
  /** Sales per calendar day across the period. */
  daily_avg: number | null
  /** Only present for `period=today`; null otherwise. */
  expected: SalesExpectation | null
  coverage: SalesCoverage
}

export interface SalesSeriesBucket {
  bucket_start: string
  /** Pre-formatted for the axis: "2026-09-11 14:00", "2026-09-07", "2026-01". */
  label: string
  sales: number | null
  cancelled: number | null
  kw: number | null
  contract_value: number | null
  contract_value_unparsed_count?: number
  cumulative_sales: number | null
  /** True on the bucket that straddles the start of coverage. */
  partial?: boolean
}

export interface SalesSeries {
  period: SalesPeriodWindow
  buckets: SalesSeriesBucket[]
  coverage: SalesCoverage
}
